// api
import type {
  NodeTimestampsSummaryOutput,
  TimestampsSummaryModel,
} from '@/api/generated/energyAnalysis/endpoints/energyData';

import { DateTime } from 'luxon';

export type TimeRangeMillis = {
  start: number;
  end: number;
};

export type SummaryEvaluation = {
  isValid: boolean;
  isInverted: boolean;
  isNoData: boolean;
  isOutOfBounds: boolean;
  adjustedRange: {
    start: DateTime;
    end: DateTime;
  } | null;
};

function toValidDateTime(value: string | null | undefined): DateTime | null {
  if (!value) {
    return null;
  }

  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed : null;
}

export function isValidSummary(
  summary?: TimestampsSummaryModel | null,
): summary is TimestampsSummaryModel {
  if (!summary) {
    return false;
  }

  return Boolean(
    toValidDateTime(summary.maxEarliestDataTimestamp) &&
    toValidDateTime(summary.minLatestDataTimestamp),
  );
}

export function mergeSummaries(
  summaries: Array<TimestampsSummaryModel | null | undefined>,
): TimestampsSummaryModel | null {
  const validSummaries = summaries.filter(isValidSummary);

  if (!validSummaries.length) {
    return null;
  }

  let maxEarliest = toValidDateTime(
    validSummaries[0].maxEarliestDataTimestamp,
  )!;
  let minLatest = toValidDateTime(validSummaries[0].minLatestDataTimestamp)!;

  for (const summary of validSummaries.slice(1)) {
    const earliest = toValidDateTime(summary.maxEarliestDataTimestamp)!;
    const latest = toValidDateTime(summary.minLatestDataTimestamp)!;

    if (earliest > maxEarliest) {
      maxEarliest = earliest;
    }

    if (latest < minLatest) {
      minLatest = latest;
    }
  }

  return {
    maxEarliestDataTimestamp: maxEarliest.toISO(),
    minLatestDataTimestamp: minLatest.toISO(),
    minEarliestDataTimestamp: null,
    maxLatestDataTimestamp: null,
  };
}

export function evaluateSummary(
  summary: TimestampsSummaryModel | null | undefined,
  timeRange?: TimeRangeMillis | null,
): SummaryEvaluation {
  if (!isValidSummary(summary)) {
    return {
      isValid: false,
      isInverted: false,
      isNoData: false,
      isOutOfBounds: false,
      adjustedRange: null,
    };
  }

  const start = toValidDateTime(summary.maxEarliestDataTimestamp)!;
  const end = toValidDateTime(summary.minLatestDataTimestamp)!;
  const isInverted = start > end;
  // Special case: same calendar date means no effective data interval.
  const isNoData = start.hasSame(end, 'day');

  if (!timeRange) {
    return {
      isValid: true,
      isInverted,
      isNoData,
      isOutOfBounds: isNoData,
      adjustedRange: {
        start,
        end,
      },
    };
  }

  const rangeStart = DateTime.fromMillis(timeRange.start);
  const rangeEnd = DateTime.fromMillis(timeRange.end);

  if (!rangeStart.isValid || !rangeEnd.isValid) {
    return {
      isValid: true,
      isInverted,
      isNoData,
      isOutOfBounds: isNoData,
      adjustedRange: {
        start,
        end,
      },
    };
  }

  const isOutOfBounds = isNoData || rangeStart < start || rangeEnd > end;
  const adjustedStart = rangeStart < start ? start : rangeStart;
  const adjustedEnd = rangeEnd > end ? end : rangeEnd;

  return {
    isValid: true,
    isInverted,
    isNoData,
    isOutOfBounds,
    adjustedRange:
      end < adjustedStart
        ? {
            start,
            end,
          }
        : {
            start: adjustedStart,
            end: adjustedEnd,
          },
  };
}

export function isSummaryOutOfBounds(
  summary: TimestampsSummaryModel | null | undefined,
  timeRange?: TimeRangeMillis | null,
): boolean {
  return evaluateSummary(summary, timeRange).isOutOfBounds;
}

export function toSummaryMap(
  allSummaries?: NodeTimestampsSummaryOutput[],
): Record<string, TimestampsSummaryModel> {
  if (!allSummaries?.length) {
    return {};
  }

  return allSummaries.reduce<Record<string, TimestampsSummaryModel>>(
    (acc, entry) => {
      if (!entry?.energyTreeNodeId || !entry?.timestampsSummary) {
        return acc;
      }
      acc[String(entry.energyTreeNodeId)] = entry.timestampsSummary;
      return acc;
    },
    {},
  );
}

export function getDescendantIds(
  nodeId: string,
  treeNodes: Array<{ energyTreeNodeId: string; parentId: string | null }> = [],
): string[] {
  const descendants: string[] = [];
  const queue = [nodeId];

  while (queue.length) {
    const current = queue.shift()!;
    const children = treeNodes
      .filter((node) => String(node.parentId) === current)
      .map((node) => String(node.energyTreeNodeId));

    descendants.push(...children);
    queue.push(...children);
  }

  return descendants;
}
